import React from 'react'
import {
  TextField,
  Autocomplete,
  MenuItem,
  Box,
  CircularProgress,
} from '@mui/material'
import { Add } from '@mui/icons-material'
import useCustomers from '../hooks/api/useCustomers'
import { blue, grey } from '@mui/material/colors'
import useIsMobile from '../hooks/useIsMobile'

const CustomerSearch = ({
  margin,
  allowAdd = false,
  onSelect,
  value,
  setValue,
  searchQuery,
  setSearchQuery,
}) => {
  const {
    customers,
    recent: recentMaster,
    ..._customers
  } = useCustomers(searchQuery, {
    include_recent: true,
  })

  const recent = recentMaster.slice(0, 3)

  const recentIds = recent.map((c) => c.id)
  const filteredCustomers = customers.filter((c) => !recentIds.includes(c.id))

  const getCustomerSearchOptions = () => {
    const createNewButton = { name: 'Create a new account', id: '__NEW__' }
    const searchErrorLabel = { name: 'Something went wrong', id: '__ERROR__' }
    const loading = { id: '__LOADING__', name: '' }
    const noResults = { name: 'No Results', id: '__EMPTY__' }

    let list = []

    if (allowAdd) {
      list.push(createNewButton)
    }

    if (_customers.isLoading) {
      list.push(loading)
      return list
    }

    if (_customers.hasLoadError) {
      list.push(searchErrorLabel)
      return list
    }

    if (recent?.length) {
      list = list.concat(recent.map((r) => ({ ...r, type: '__RECENT__' })))
    }

    list = list.concat(filteredCustomers)

    if (list.length === 0 || (allowAdd && list.length === 1))
      list.push(noResults)

    return list
  }

  const options = getCustomerSearchOptions()

  const getFirstRecentIndex = () => {
    return options.map((c) => c.type).indexOf('__RECENT__')
  }

  const getFirstNonRecentIndex = () => {
    const found = options.find(
      (c) => c.type !== '__RECENT__' && typeof c.id === 'number'
    )

    return options.indexOf(found)
  }

  const firstRecentIndex = getFirstRecentIndex()
  const firstNonRecentIndex = getFirstNonRecentIndex()

  const hasRecentlyViewed = recent?.length > 0 && !searchQuery

  const isMobile = useIsMobile()

  return (
    <Autocomplete
      fullWidth
      value={value}
      onChange={() => {}}
      options={options}
      renderOption={({ ['data-option-index']: index }, option) => {
        const isCreateButton = option.id === '__NEW__'
        const isErrorLabel = option.id === '__ERROR__'
        const isLoading = option.id === '__LOADING__'
        const noResults = option.id === '__EMPTY__'

        const isFirstNonRecent =
          index === firstNonRecentIndex && hasRecentlyViewed

        if (isLoading)
          return (
            <Box
              key={option.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CircularProgress />
            </Box>
          )

        if (noResults)
          return (
            <Box
              key={option.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <i>No Results</i>
            </Box>
          )

        return (
          <Box key={option.id}>
            {index === firstRecentIndex && hasRecentlyViewed && (
              <Box
                sx={{
                  borderTop: `1px solid ${grey[400]}`,
                  mb: 1,
                  pl: 2,
                  pt: 1,
                  color: blue[800],
                }}
              >
                <i>Recently viewed</i>
                <br></br>
              </Box>
            )}
            <MenuItem
              onClick={() => {
                onSelect(option)
              }}
              sx={{
                color: isErrorLabel ? 'red' : null,
                pt: isFirstNonRecent ? 1 : 0,
                mt: isFirstNonRecent ? 1 : 0,
                borderTop: isFirstNonRecent ? `1px solid ${grey[400]}` : 'none',
              }}
            >
              {isCreateButton && (
                <Add sx={{ ml: '-8px', mr: '4px' }} color='primary' />
              )}

              {option.name}
            </MenuItem>
          </Box>
        )
      }}
      filterOptions={(x) => x}
      renderInput={(params) => (
        <TextField
          onKeyDown={(e) => e.stopPropagation()}
          margin={margin}
          value={searchQuery}
          fullWidth={isMobile}
          sx={{ minWidth: isMobile ? null : '390px' }}
          label={'Search for an account'}
          onChange={(e, ...args) => {
            if (params.onChange) params.onChange(e, ...args)
            // searchCustomer(e.target.value)
            setSearchQuery(e.target.value)
          }}
          {...params}
        />
      )}
      getOptionLabel={(option) => option.name}
    ></Autocomplete>
  )
}

export default CustomerSearch
